import { useState, useMemo, useRef, useEffect } from 'react';
import { IconType } from 'components/Icon';
import Icon from 'components/Icon';
import { DesignSystemSection, DsLink } from 'designSystem/types';
import { DS_TAB_CHROME } from 'designSystem/config';
import { useTheme } from 'styled-components';
import { useSelector } from 'react-redux';
import { RootReducer } from 'redux/reducers';
import useDsNavigation from 'hooks/useDsNavigation';
import * as S from './styles';

const DROPDOWN_WIDTH = 200;
const SUBMENU_WIDTH = 180;
const EDGE_PADDING = 8;

// Tab order for AddTabButton dropdown (subset that appears as tabs)
const ORDERED_SECTIONS: DesignSystemSection[] = [
	'foundations',
	'guidelines',
	'theme',
	'styles',
	'components',
	'gallery',
	'assets',
];

const AddTabButton = () => {
	const { openTab } = useDsNavigation();
	const designSystemContent = useSelector((r: RootReducer) => r.mode.designSystemContent);
	const theme = useTheme();
	const [showAddTabDropdown, setShowAddTabDropdown] = useState(false);
	const [dropdownLeft, setDropdownLeft] = useState(0);
	const [openLeft, setOpenLeft] = useState(false);
	const baseRef = useRef<HTMLDivElement>(null);

	// Calculate dropdown position when it opens
	useEffect(() => {
		if (showAddTabDropdown && baseRef.current) {
			const rect = baseRef.current.getBoundingClientRect();
			const viewportWidth = window.innerWidth;

			// Where the dropdown's right edge would be if left-aligned with button
			const dropdownRightEdge = rect.left + DROPDOWN_WIDTH;

			if (dropdownRightEdge + EDGE_PADDING <= viewportWidth) {
				// Normal case: dropdown fits, align left edge with button
				setDropdownLeft(0);
				// Check if submenus would fit
				const submenuRightEdge = dropdownRightEdge + SUBMENU_WIDTH;
				setOpenLeft(submenuRightEdge + EDGE_PADDING > viewportWidth);
			} else {
				// Tight: push dropdown to stick to viewport right edge
				const offset = viewportWidth - EDGE_PADDING - DROPDOWN_WIDTH - rect.left;
				setDropdownLeft(offset);
				setOpenLeft(true);
			}
		}
	}, [showAddTabDropdown]);

	//----------------------------------------------------------------------
	// UTILITY
	//----------------------------------------------------------------------

	const getSubItems = useMemo(() => {
		return (section: DesignSystemSection): DsLink[] | null => {
			switch (section) {
				case 'guidelines':
					return (designSystemContent?.guidelines || []).map((g) => ({
						label: g.title,
						contentPath: g.path,
					}));
				case 'components': {
					const components = designSystemContent?.components?.all || [];
					if (components.length === 0) return null;
					return [
						{ label: 'All Components', contentPath: undefined },
						...components.map((c) => ({ label: c.name, contentPath: c.id })),
					];
				}
				case 'gallery': {
					const gallery = designSystemContent?.gallery || [];
					if (gallery.length === 0) return null;
					return [
						{ label: 'Complete Gallery', contentPath: undefined },
						...gallery.map((g) => ({ label: g.title, contentPath: g.path })),
					];
				}
				case 'theme':
					return null;
				default:
					return null;
			}
		};
	}, [designSystemContent]);

	//----------------------------------------------------------------------
	// RENDERING
	//----------------------------------------------------------------------

	return (
		<S.Base
			ref={baseRef}
			onMouseEnter={() => setShowAddTabDropdown(true)}
			onMouseLeave={() => setShowAddTabDropdown(false)}
		>
			<S.Button>
				<Icon type={IconType.ADD} width={16} height={16} />
			</S.Button>
			{showAddTabDropdown && (
				<S.Dropdown $leftOffset={dropdownLeft} $openLeft={openLeft}>
					{ORDERED_SECTIONS.map((section) => {
						const chrome = DS_TAB_CHROME[section];
						const subItems = getSubItems(section);
						const hasSubItems = subItems && subItems.length > 0;

						return (
							<S.DropdownItemWrapper key={section}>
								<S.DropdownItem
									$hasSubmenu={!!hasSubItems}
									onClick={() => !hasSubItems && openTab(section)}
								>
									<S.DropdownItemContent>
										<Icon type={chrome.icon} width={16} height={16} color={theme.gray[900]} />
										{chrome.label}
									</S.DropdownItemContent>
									{hasSubItems && (
										<Icon
											type={IconType.LINE_ARROW_RIGHT}
											width={12}
											height={12}
											color={theme.gray[600]}
										/>
									)}
								</S.DropdownItem>
								{hasSubItems && (
									<S.Submenu $openLeft={openLeft}>
										{subItems.map((item) => (
											<S.SubmenuItem
												key={item.contentPath || 'all'}
												onClick={() => openTab(section, item.contentPath)}
											>
												{item.label}
											</S.SubmenuItem>
										))}
									</S.Submenu>
								)}
							</S.DropdownItemWrapper>
						);
					})}
				</S.Dropdown>
			)}
		</S.Base>
	);
};

export default AddTabButton;
